import { useState } from 'react';
import Hero from './components/Hero';
import USPSection from './components/USPSection';
import WorkflowSection from './components/WorkflowSection';
import TargetAudienceSection from './components/TargetAudienceSection';
import ArchitectureSection from './components/ArchitectureSection';
import CasesSection from './components/CasesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import SEOSchema from '../../components/SEOSchema';

export default function HomePage() {
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactClick = () => {
    setShowContactForm(true);
    setSubmitStatus('idle');
  };

  const handleCloseContact = () => {
    setShowContactForm(false);
    setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncodedData = new URLSearchParams();

    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d4f0ut6iti2a337fh2sg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOSchema type="website" />
      <SEOSchema type="organization" />
      <Hero onContactClick={handleContactClick} />
      <USPSection />
      <WorkflowSection />
      <TargetAudienceSection />
      <ArchitectureSection />
      <CasesSection />
      <CTASection onContactClick={handleContactClick} />
      <Footer />

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Contact Opnemen</h3>
                <button
                  onClick={handleCloseContact}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              {submitStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Bericht Verzonden!</h4>
                  <p className="text-gray-600 mb-6">Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.</p>
                  <button
                    onClick={handleCloseContact}
                    className="bg-teal-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Sluiten
                  </button>
                </div>
              ) : (
                <form 
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Voornaam *
                      </label>
                      <input
                        type="text"
                        name="voornaam"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Uw voornaam"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Achternaam *
                      </label>
                      <input
                        type="text"
                        name="achternaam"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Uw achternaam"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      E-mailadres *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="uw.email@bedrijf.nl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Organisatie *
                    </label>
                    <input
                      type="text"
                      name="organisatie"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Naam van uw organisatie"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ik ben een *
                    </label>
                    <select
                      name="rol"
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="">Selecteer uw rol</option>
                      <option value="Gemeente">Gemeente</option>
                      <option value="Vervoersbedrijf">Vervoersbedrijf</option>
                      <option value="Andere">Andere</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Bericht
                    </label>
                    <textarea
                      name="bericht"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Vertel ons over uw specifieke vervoersvraagstukken..."
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1">Maximaal 500 karakters</div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy_akkoord"
                      value="Ja"
                      required
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-600">
                      Ik ga akkoord met de verwerking van mijn gegevens voor het beantwoorden van mijn vraag. *
                    </label>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                      Er is iets misgegaan. Probeer het opnieuw.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-500 text-white py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Verzenden...' : 'Verstuur Bericht'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
